import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import MicrosoftCallback from "@/pages/MicrosoftCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Accounts from "@/pages/Accounts";
import AccountDetail from "@/pages/AccountDetail";
import Deals from "@/pages/Deals";
import Renewals from "@/pages/Renewals";
import Projects from "@/pages/Projects";
import LicenceUsage from "@/pages/LicenceUsage";
import Billing from "@/pages/Billing";
import Tickets from "@/pages/Tickets";
import Stax from "@/pages/Stax";
import Vendors from "@/pages/Vendors";
import Reports from "@/pages/Reports";
import TechnicalReports from "@/pages/TechnicalReports";
import Admin from "@/pages/Admin";
import People from "@/pages/People";
import Leads from "@/pages/Leads";
import Outbound from "@/pages/Outbound";
import SalesTools from "@/pages/SalesTools";
import Marketing from "@/pages/Marketing";
import Onboarding from "@/pages/Onboarding";
import OnboardingTemplateEditor from "@/pages/OnboardingTemplateEditor";
import QuoteBuilder from "@/pages/QuoteBuilder";
import SupportConfig from "@/pages/SupportConfig";
import TicketDetail from "@/pages/TicketDetail";
import WallDisplay from "@/pages/WallDisplay";
import WallDisplayAdmin from "@/pages/WallDisplayAdmin";
import ThreatAdvisories from "@/pages/ThreatAdvisories";

import { PortalAuthProvider } from "@/portal/portalAuth";
import PortalLogin from "@/portal/PortalLogin";
import PortalAuthCallback from "@/portal/PortalAuthCallback";
import PortalLayout from "@/portal/PortalLayout";
import PortalDashboard from "@/portal/PortalDashboard";
import PortalTickets from "@/portal/PortalTickets";
import PortalProjects from "@/portal/PortalProjects";
import PortalProducts from "@/portal/PortalProducts";
import PortalInvoices from "@/portal/PortalInvoices";
import PortalCyberPosture from "@/portal/PortalCyberPosture";
import PortalMicrosoftCallback from "@/portal/PortalMicrosoftCallback";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <div className="h-2 w-2 rounded-full bg-blue-600 pulse-dot" />
          <span className="font-mono text-sm">Loading Xentra…</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // OAuth fragment routing — branch between staff and portal callbacks based on path.
  if (location.hash?.includes("session_id=")) {
    if (location.pathname.startsWith("/portal")) {
      return <PortalAuthCallback />;
    }
    return <AuthCallback />;
  }

  // Public office wall display — no auth required, slug-gated on the backend
  if (location.pathname.startsWith("/wall/")) {
    return (
      <Routes>
        <Route path="/wall/:slug" element={<WallDisplay />} />
      </Routes>
    );
  }

  // Portal section runs under its own auth provider
  if (location.pathname.startsWith("/portal")) {
    return (
      <PortalAuthProvider>
        <Routes>
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/auth/callback" element={<PortalAuthCallback />} />
          <Route path="/portal/auth/microsoft/callback" element={<PortalMicrosoftCallback />} />
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<Navigate to="/portal/dashboard" replace />} />
            <Route path="dashboard" element={<PortalDashboard />} />
            <Route path="tickets" element={<PortalTickets />} />
            <Route path="projects" element={<PortalProjects />} />
            <Route path="products" element={<PortalProducts />} />
            <Route path="cyber-posture" element={<PortalCyberPosture />} />
            <Route path="invoices" element={<PortalInvoices />} />
          </Route>
          <Route path="*" element={<Navigate to="/portal/login" replace />} />
        </Routes>
      </PortalAuthProvider>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
      <Route
        path="/app"
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="accounts/:id" element={<AccountDetail />} />
        <Route path="leads" element={<Leads />} />
        <Route path="outbound" element={<Outbound />} />
        <Route path="deals" element={<Deals />} />
        <Route path="renewals" element={<Renewals />} />
        <Route path="projects" element={<Projects />} />
        <Route path="licence-usage" element={<LicenceUsage />} />
        <Route path="billing" element={<Billing />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="tickets/:ticketId" element={<TicketDetail />} />
        <Route path="threat-advisories" element={<ThreatAdvisories />} />
        <Route path="support" element={<Navigate to="/app/tickets" replace />} />
        <Route path="support/config" element={<SupportConfig />} />
        <Route path="stax" element={<Stax />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="sales-tools" element={<SalesTools />} />
        <Route path="sales-tools/quotes" element={<QuoteBuilder />} />
        <Route path="marketing" element={<Marketing />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="onboarding/templates/new" element={<OnboardingTemplateEditor />} />
        <Route path="onboarding/templates/:id/edit" element={<OnboardingTemplateEditor />} />
        <Route path="people" element={<People />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/technical" element={<TechnicalReports />} />
        <Route path="admin" element={<Admin />} />
        <Route path="admin/wall-display" element={<WallDisplayAdmin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
